<template>
  <div class="rating-block">
    <div class="rating-block__header">
      <div
        class="rating-block__logo"
        :class="label && 'rating-block__logo--' + label"
        aria-hidden="true"
      ></div>
      <button
        v-if="source.rating"
        :id="'rating-block-details-' + randomId"
        class="rating-block__details-btn"
        :aria-controls="'rating-block-panel-' + randomId"
        :aria-expanded="areDetailsExpanded"
        :aria-label="t('rating-toggle-details')"
        @click="onDetailsToggle"
      >
        <i class="icon icon-Down" aria-hidden="true"></i>
      </button>
    </div>
    <template v-if="source.rating">
      <div class="rating-block__rating">
        <div class="rating-block__stars">
          <span
            v-for="(star, index) in starsElements"
            :key="index"
            :class="['rating-block__star', star]"
          >
            <i class="icon icon-Star" aria-hidden="true"></i>
            <i v-if="star === 'half'" class="icon icon-Star" aria-hidden="true"></i>
          </span>
        </div>
        <div class="sr-only">
          {{ source.reviewsNumber }} reviews. Rating: {{ source.rating }} out of
          {{ scale }}
        </div>
        <div class="rating-block__score" aria-hidden="true">
          <span class="rating-block__score-value">{{ source.rating }}</span>
          <span v-if="source.reviewsNumber" class="rating-block__score-reviews">
            - {{ source.reviewsNumber }} {{ t('reviews-reviews') }}
          </span>
        </div>
      </div>
      <div
        :id="'rating-block-panel-' + randomId"
        :aria-labelledby="'rating-block-details-' + randomId"
        :aria-hidden="!areDetailsExpanded"
        role="tabpanel"
        class="rating-block__details"
        :style="{
          '--rating-block-details-height': contentHeight ? `${contentHeight}px` : null,
        }"
      >
        <div ref="content" class="rating-block__details-content">
          <div v-for="(detail, index) in details" :key="index" class="rating-block__details-row">
            <i :class="['icon', detail.icon]" aria-hidden="true"></i>
            <span class="rating-block__details-percent" aria-hidden="true">
              {{ detail.percent }}%
            </span>
            <span class="rating-block__details-type" aria-hidden="true">{{ detail.type }}</span>
            <div class="sr-only">{{ detail.type }} ratings: {{ detail.percent }}%</div>
          </div>
        </div>
      </div>
    </template>
    <div v-else class="rating-block__loader">
      <div v-if="isError" class="rating-block__error">
        {{ t('rating-loading-error') }}
      </div>
      <template v-if="isLoading">
        <div aria-hidden="true" class="rating-block__dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <span>{{ t('rating-loading') }}</span>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getStarRating } from '../../../../utils/getStarRating';
import { useReputationSummaryQuery } from '../../../../utils/queries/useReputationQuery';
import useId from '../../../../composables/useId';

const randomId = useId();

const { t } = useI18n();

const emit = defineEmits(['details-expand']);

const props = defineProps({
  venueId: {
    type: [String, Number],
    required: true,
  },
  sourceId: {
    type: String,
    required: true,
  },
  label: {
    type: String,
  },
  scale: {
    type: Number,
    default: () => 5,
  },
});
const TRANSITION = 200;

const { data, isError, isLoading } = useReputationSummaryQuery(props.venueId);

const content = ref(null);
const contentHeight = ref(null);
const areDetailsExpanded = ref(false);

const onDetailsToggle = () => {
  contentHeight.value = content.value.offsetHeight;
  !areDetailsExpanded.value && emit('details-expand');
  setTimeout(() => (areDetailsExpanded.value = !areDetailsExpanded.value), 0);
  setTimeout(() => (contentHeight.value = null), TRANSITION);
};

const getPercentage = (value) => Math.round((value * 100) / source.value.reviewsNumber);

// if the sum of rounded percentages is not equal to 100, adjust the rounded percentages
const adjustRoundedPercentages = (sentiment) => {
  const sumRounded = sentiment.reduce((sum, item) => sum + item.percent, 0);
  return sumRounded === 100
    ? sentiment
    : sentiment.map((item, index) => {
        return index === sentiment.length - 1
          ? { ...item, percent: item.percent + (100 - sumRounded) }
          : item;
      });
};

const starsElements = computed(() => getStarRating(source.value.rating, props.scale));

const source = computed(() => {
  const filteredSource = data?.value?.summary?.sources?.find(
    (source) => source.id === props.sourceId
  );

  if (filteredSource)
    return {
      rating: filteredSource.rating,
      sentiment: filteredSource.sentimentCount,
      reviewsNumber: filteredSource.count,
    };

  return {
    rating: null,
    sentiment: null,
    reviewsNumber: null,
  };
});

const details = computed(() => {
  if (!source.value.reviewsNumber || !source.value.sentiment) return null;
  const sentiment = [];

  if (source.value.sentiment.positive) {
    sentiment.push({
      icon: 'icon-Thumbs-up',
      percent: getPercentage(source.value.sentiment.positive),
      type: t('rating-positive'),
    });
  }

  if (source.value.sentiment.neutral) {
    sentiment.push({
      icon: 'icon-Face-neutral',
      percent: getPercentage(source.value.sentiment.neutral),
      type: t('rating-neutral'),
    });
  }

  if (source.value.sentiment.negative) {
    sentiment.push({
      icon: 'icon-Thumbs-down',
      percent: getPercentage(source.value.sentiment.negative),
      type: t('rating-negative'),
    });
  }
  return adjustRoundedPercentages(sentiment);
});
</script>

<style lang="scss">
@import './scss/RatingBlock.scss';
</style>
